import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Trash2, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

function StarPicker({ value, onChange, size = "md", interactive = true }) {
  const [hover, setHover] = useState(0);
  const cls = size === "lg" ? "w-7 h-7" : size === "sm" ? "w-3.5 h-3.5" : "w-5 h-5";
  return (
    <div className={`inline-flex items-center gap-1 ${interactive ? "cursor-pointer" : ""}`} role="radiogroup" aria-label="Rating">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = (hover || value) >= n;
        return (
          <button
            key={n}
            type="button"
            disabled={!interactive}
            onClick={() => interactive && onChange(n)}
            onMouseEnter={() => interactive && setHover(n)}
            onMouseLeave={() => interactive && setHover(0)}
            className="transition-transform hover:scale-110 disabled:cursor-default"
            data-testid={interactive ? `star-${n}` : undefined}
            aria-checked={value === n}
            role="radio"
          >
            <Star className={`${cls} ${filled ? "text-sponsored fill-sponsored" : "text-neutral-300"}`} />
          </button>
        );
      })}
    </div>
  );
}

export default function ReviewsSection({ venueId, onRatingChange }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const myReview = user ? reviews.find((r) => r.user_id === user.id) : null;

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/venues/${venueId}/reviews`);
      setReviews(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [venueId]);

  useEffect(() => {
    if (myReview) {
      setRating(myReview.rating);
      setComment(myReview.comment || "");
    }
  }, [myReview]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error("Pick 1–5 stars"); return; }
    setBusy(true);
    try {
      await api.post(`/venues/${venueId}/reviews`, { rating, comment });
      toast.success(myReview ? "Review updated" : "Thanks for the review!");
      await reload();
      onRatingChange && onRatingChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save review");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/reviews/${id}`);
      setRating(0);
      setComment("");
      await reload();
      onRatingChange && onRatingChange();
      toast.success("Review removed");
    } catch (err) {
      toast.error("Could not remove review");
    }
  };

  return (
    <section className="mt-12" data-testid="reviews-section">
      <h2 className="font-heading text-2xl text-navy mb-6">Reviews</h2>

      {/* Write/edit form */}
      {user ? (
        <form onSubmit={submit} className="bg-white rounded-2xl border border-black/5 p-6 mb-8 shadow-sm">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="text-sm text-navy font-medium">
              {myReview ? "Update your review" : "How was it?"}
            </div>
            <StarPicker value={rating} onChange={setRating} size="lg" />
          </div>
          <Textarea
            rows={3}
            placeholder="Share what you loved (or didn't)…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            data-testid="review-comment"
          />
          <div className="flex justify-between items-center mt-4 gap-3">
            {myReview ? (
              <button
                type="button"
                onClick={() => remove(myReview.id)}
                className="text-xs flex items-center gap-1 text-rose-500 hover:text-rose-700"
                data-testid="review-delete"
              >
                <Trash2 className="w-3 h-3" /> Remove my review
              </button>
            ) : <span />}
            <Button type="submit" disabled={busy} className="rounded-full bg-navy hover:bg-navy/90" data-testid="review-submit">
              {busy ? "Saving…" : (myReview ? "Save changes" : "Post review")}
            </Button>
          </div>
        </form>
      ) : (
        <div className="bg-sand/20 border border-sand/40 rounded-2xl p-5 mb-8 flex items-center justify-between flex-wrap gap-3">
          <span className="text-navy/80 text-sm">Sign in to leave a review.</span>
          <Link to="/login" className="rounded-full bg-navy text-white px-4 py-1.5 text-sm hover:bg-navy/90">Sign in</Link>
        </div>
      )}

      {/* Review list */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-navy" /></div>
      ) : reviews.length === 0 ? (
        <p className="text-neutral-500 italic">Be the first to leave a review.</p>
      ) : (
        <div className="space-y-4">
          {reviews.map((r, i) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="bg-white rounded-2xl border border-black/5 p-5"
              data-testid={`review-${r.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium text-navy">{r.user_name}</div>
                  <div className="flex items-center gap-2 text-xs text-neutral-500 mt-0.5">
                    <StarPicker value={r.rating} onChange={() => {}} size="sm" interactive={false} />
                    <span>·</span>
                    <span>{new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
                  </div>
                </div>
              </div>
              {r.comment && <p className="text-neutral-700 leading-relaxed mt-3">{r.comment}</p>}
            </motion.div>
          ))}
        </div>
      )}
    </section>
  );
}
